import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
  RefObject,
} from 'react';
import { OutboundDocumentAPI } from '../../common/api/outboundDocument.ts';
import { ContactType, OutboundDocumentStatusEnum } from '../../common/enums.ts';
import queryHooks from '../../common/queryHooks/index.ts';
import mutationHooks from '../../common/mutationHooks/index.ts';

interface IProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  outboundDocument?: OutboundDocumentAPI.Response.ReadOneById;
}

const ResendEpcisDocumentModal = ({
  isOpen,
  setIsOpen,
  outboundDocument,
}: IProps) => {
  const modal = useRef<HTMLDivElement>(null);
  const methodSelect = useRef<HTMLSelectElement>(null);
  const recipientEmailAddressesSelect = useRef<HTMLSelectElement>(null);

  const { data: tradingPartnerContacts } =
    queryHooks.tradingPartner.useReadAllContactsByOutboundDocumentId(
      outboundDocument?.id
    );

  const { mutate: resend } =
    mutationHooks.outboundDocument.useUpdateResendOneById();

  const initValues = () => ({
    method: 'as2',
    recipientEmailAddresses: [] as string[],
  });

  const [values, setValues] = useState(initValues());
  const [contactList, setContactList] = useState<string[]>([]);

  const isAs2AllowedMethod = useMemo<boolean>(() => {
    if (!outboundDocument) return true;
    const { outboundDocumentStatuses } = outboundDocument;
    const numberOfTries =
      outboundDocumentStatuses?.filter(({ status }) =>
        [
          OutboundDocumentStatusEnum.failed.toString(),
          OutboundDocumentStatusEnum.incomplete.toString(),
        ].includes(status.toLocaleLowerCase())
      ).length ?? 0;

    setValues(prev => ({
      ...prev,
      method: numberOfTries < 3 ? 'as2' : 'email',
    }));

    return numberOfTries < 3;
  }, [outboundDocument]);

  useEffect(() => {
    const { current } = modal;
    if (current) {
      M.Modal.init(current, {
        onCloseEnd: () => setIsOpen(false),
      });
      return () => M.Modal.getInstance(current)?.destroy();
    }
  }, [modal, setIsOpen]);

  const initSelect = (ref: RefObject<HTMLSelectElement | null>) => {
    const { current } = ref;
    if (current) {
      M.FormSelect.init(current);
      return () => M.FormSelect.getInstance(current)?.destroy();
    }
  };

  useEffect(() => {
    initSelect(methodSelect);
  }, [methodSelect]);

  useEffect(() => {
    initSelect(recipientEmailAddressesSelect);
  }, [recipientEmailAddressesSelect, contactList, values.method]);

  const getModal = useCallback(
    () => (modal.current ? M.Modal.getInstance(modal.current) : null),
    []
  );
  const openModal = useCallback(() => getModal()?.open(), [getModal]);
  const closeModal = useCallback(() => getModal()?.close(), [getModal]);

  useEffect(() => {
    if (isOpen) openModal();
    else closeModal();
  }, [isOpen, closeModal, openModal]);

  const onChange = ({
    target: { id, value },
  }: ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    if (id !== 'recipientEmailAddresses')
      return setValues(prev => ({ ...prev, [id]: value }));

    const { current } = recipientEmailAddressesSelect;
    if (current) {
      const selections = M.FormSelect.getInstance(current)?.getSelectedValues();
      setValues(prev => ({ ...prev, [id]: selections as string[] }));
    }
  };

  useEffect(() => {
    if (tradingPartnerContacts) {
      const arr = tradingPartnerContacts
        .flatMap(({ contactDetails }) => contactDetails)
        .filter(cd => cd?.contactType === ContactType.email)
        .map(cd => cd?.contactDetail)
        .filter(x => x !== undefined);

      setContactList(arr);
    }
  }, [tradingPartnerContacts]);

  const resendFile = useCallback(() => {
    const { method, recipientEmailAddresses } = values;
    resend({
      outboundDocumentId: outboundDocument?.id || 0,
      method,
      recipientEmailAddresses,
    });
    closeModal();
  }, [values, outboundDocument, closeModal, setValues, resend]);

  return (
    <div ref={modal} className="modal">
      <div className="modal-content">
        <div className="row">
          <h5 className="center">Resend EPCIS Document</h5>
          <div className="divider" />
        </div>
        <div className="row">
          {isAs2AllowedMethod ? (
            <div className="input-field col s12">
              <select ref={methodSelect} id="method" onChange={onChange}>
                <option value="as2">AS2</option>
                <option value="email">Email</option>
              </select>
              <label htmlFor="method">Communication Method</label>
            </div>
          ) : (
            <p>
              AS2 has failed numerous times. Please send via email, or contact
              your MedTrax service representative.
            </p>
          )}
        </div>
        <div className="row">
          {values.method === 'email' && (
            <div className="input-field col s12">
              <select
                multiple
                ref={recipientEmailAddressesSelect}
                id="recipientEmailAddresses"
                value={values.recipientEmailAddresses}
                onChange={onChange}
              >
                {contactList.map(emailAddress => (
                  <option key={emailAddress} value={emailAddress}>
                    {emailAddress}
                  </option>
                ))}
              </select>
              <label htmlFor="recipientEmailAddresses">
                Recipient Email Addresses
              </label>
            </div>
          )}
        </div>
        <div className="row">
          <div className="col s12 m4 offset-m4">
            <button
              className="btn-small blue white-text waves-effect waves-light col s12"
              disabled={
                values.method === 'email' &&
                !values.recipientEmailAddresses.length
              }
              onClick={resendFile}
            >
              Resend EPCIS
            </button>
          </div>
          <div className="col s12 m4">
            <button
              className="btn-small white blue-text waves-effect waves-dark col s12"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResendEpcisDocumentModal;
